import { JSONValue } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useMemo } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { PillListActions } from 'src/components/core/PillsInput';

import { Button, Flex, Input, Label, Loading, Pills, Popover, Separator, Text, TextField } from '@/components/core';
import { CloseIcon } from '@/components/icons';
import { findField, FormatterValidatorHeader } from '@/components/ui';
import { useFormatterMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';

import { FormatterTest } from './FormatterTest';

export type ClassificationLabel = {
  value: string;
  description: string;
};

export type ClassificationFormatterProps = {
  fieldId: string;
  formatterId: string;
};

const IClassificationFormatter = ({ fieldId, formatterId }: ClassificationFormatterProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useFormatterMutation({ fieldId, formatterId, parentId: field?.parentId });
  const formatter = field?.formatters?.find((f) => f.id === formatterId);

  const onItemsChange = useCallback(
    (items: Pills.Item[]) => {
      return mutate(
        (f) =>
          (f.config.labels = items.map((it) => ({
            value: it.value,
            description: it.label ?? it.value,
          })))
      );
    },
    [mutate]
  );

  const onSetCatchAll = useCallback(
    (value?: string) => {
      mutate((f) => (f.config.catchAll = value as JSONValue));
    },
    [mutate]
  );

  const initialValues: Pills.Item[] = useMemo(
    () =>
      ((formatter?.config?.labels ?? []) as ClassificationLabel[]).map(
        ({ value, description }: ClassificationLabel) => ({
          value: value,
          label: description,
        })
      ),
    [formatter?.config?.labels]
  );

  if (!formatter) return <Loading message="Loading classification formatter" />;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader formatter={formatter} />

      <Separator orientation="horizontal" />
      <div className="gap-xs flex grow flex-col">
        <TextField>
          <Label>Categories</Label>

          <Pills.Input>
            <Pills.List
              className="gap-xs flex grow"
              autoSeparate={['+', '-', '*', '/']}
              placeholder='Add a category (e.g. "EUR" or "USD")'
              onItemsChange={onItemsChange}
              initialValues={initialValues}
            >
              {(pill: Pills.Item, handle: PillListActions) => {
                return (
                  <DialogTrigger key={pill.value}>
                    <Button variant="secondary" className="gap-xs flex py-0">
                      {pill.value}
                      <CloseIcon onClick={() => handle.removeItem(pill)} />
                    </Button>

                    <Popover className="p-2">
                      <Flex col className="gap-xs">
                        <Label>Description</Label>
                        <Input
                          value={pill.label}
                          onChange={(e) => handle.updateItem({ label: e.target.value, value: pill.value })}
                        />
                      </Flex>
                    </Popover>
                  </DialogTrigger>
                );
              }}
            </Pills.List>
          </Pills.Input>
          <Text slot="description">An exhaustive list of all the values this field can have.</Text>
        </TextField>

        <TextField
          onInput={(e) => onSetCatchAll(e.currentTarget.value)}
          defaultValue={formatter?.config?.catchAll as string | undefined}
        >
          <Label>Catch-all value</Label>
          <Input placeholder="Unknown" />
          <Text slot="description">If set, this value will be used if no other categories are found.</Text>
        </TextField>
      </div>

      <FormatterTest fieldId={fieldId} formatterId={formatterId} />
    </div>
  );
};

export const ClassificationFormatter = memo(IClassificationFormatter) as typeof IClassificationFormatter;
